import { type FC } from "react";
import { ArrowRight, Map, Settings, FolderOpen, Info, Star } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import { LAWYER_PLANS } from "@/shared/constants/lawyerPlans";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";

interface ClientInviteWelcomeProps {
  planKey: LawyerPlanKey | null;
  lawyerName: string;
  onGetStarted: () => void;
}

const NEXT_STEPS = [
  {
    icon: Map,
    title: "Your roadmap takes shape",
    description: "Elayne sorts what you add into your settlement roadmap",
  },
  {
    icon: Settings,
    title: "Hand off the busywork",
    description: "Authorize Elayne to handle admin work for you",
  },
  {
    icon: FolderOpen,
    title: "[lawyer] gets an organized record",
    description: "What you add is review-ready, so their hours go to legal work",
  },
];

export const ClientInviteWelcome: FC<ClientInviteWelcomeProps> = ({
  planKey,
  lawyerName,
  onGetStarted,
}) => {
  const planName = planKey ? LAWYER_PLANS[planKey].name : null;
  const steps = NEXT_STEPS.map((s) => ({
    ...s,
    title: s.title.replace("[lawyer]", lawyerName),
  }));

  return (
    <div className="flex h-screen bg-subtle">
      <div className="relative flex flex-1 flex-col items-center justify-center overflow-y-auto p-4 sm:p-10">
        <img src={logoFull} alt="Elayne" className="absolute left-4 top-4 h-8 sm:left-8 sm:top-8" />

        <div className="flex w-full max-w-[500px] flex-col items-center gap-6">
          {planName && (
            <Tag color="white" icon={Star}>
              Your plan: {planName}
            </Tag>
          )}

          <div className="flex flex-col gap-2 text-center">
            <h2>Welcome to Elayne</h2>
            <p className="text-sm text-secondary">
              Your workspace is ready, and the first steps only take a few minutes
            </p>
          </div>

          <div className="w-full rounded-lg border border-primary bg-white p-6">
            <div className="flex flex-col gap-4">
              <span className="text-sm font-semibold text-secondary">What happens next</span>
              <div className="flex flex-col gap-4">
                {steps.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className="flex items-center gap-3">
                      <Icon className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">{step.title}</span>
                        <span className="text-sm text-secondary">{step.description}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <Button variant="brand" rounded onClick={onGetStarted} className="w-full">
            Get started
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>

          <div className="flex w-full gap-3 rounded-lg border border-primary bg-background p-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
              <Info className="h-4 w-4 text-primary" aria-hidden="true" />
            </div>
            <p className="text-sm text-secondary">
              You have full control of who has access, and you can add or remove anyone.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
